import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {DcpGlobalMessageService} from '@app/core/services/dcp-global-message.service';
import {ConfigService, HttpRequestOptions, KymaService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BaseSiteService, Product} from '@spartacus/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, mergeMap, share, switchMap} from 'rxjs/operators';
import {DcpConsentTemplateData} from '../model/dcp-consent-template-data';
import {LeadFormCreator, LeadFormModel} from '../product/cta-form/lead-form/lead-form.model';
import {G2ReviewList} from '../product/models/g2-review.model';
import {DcpProductUtilitiesService} from '../product/services/dcp-product-utilities.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpKymaService {
  private anonymousUserId = 'anonymous';
  private currentUserId = 'current';
  private partnerProductConsentTemplateId = 'PARTNERPRODUCT';
  private defaultConsentTemplateVersion = '1';
  private baseSiteIdHeaderName = 'baseSiteId';
  private userIdHeaderName = 'userId';
  private cartIdHeaderName = 'cartId';
  private reviewsData = new BehaviorSubject<G2ReviewList>(null);

  constructor(
    private http: HttpClient,
    private productUtilitiesService: DcpProductUtilitiesService,
    private configService: ConfigService,
    private baseSiteService: BaseSiteService,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private dcpKymaServiceConfigurator: KymaService
  ) {}

  public saveTaxCertificateToCurrentCart(file: File): Observable<{code: string}> {
    const form = new FormData();
    form.append('taxCertificate', file);
    return this.getSaveFileToCartCallOptions().pipe(
      switchMap((params) =>
        this.http.post<{code: string}>(
          this.dcpKymaServiceConfigurator.getUrl('fileService') + '/addTaxCertificateToCart',
          form,
          params
        )
      )
    );
  }

  public removeTaxCertificateFromCurrentCart(fileCode: string): Observable<{}> {
    return this.getRemoveFileFromCartCallOptions().pipe(
      mergeMap((paramsHeaders) => {
        const newparams: any = Object.assign(
          {},
          {body: {fileCode}},
          {headers: paramsHeaders.headers},
          {params: paramsHeaders.params}
        );
        return this.http.delete<{}>(
          this.dcpKymaServiceConfigurator.getUrl('fileService') + '/removeTaxCertificateFromCart',
          newparams
        );
      })
    );
  }

  /**
   * Call to Kyma lambda to check if there's a consent exists for current product, current user,
   * 'PARTNERPRODUCT' consent template id and 'dcp' base site id.
   *
   * @param user - user to take current user's data from.
   * @param productId - entered product page's product id.
   */
  public getConsentTemplate(user: LeadFormCreator, productId: string): Observable<DcpConsentTemplateData> {
    return this.dcpKymaServiceConfigurator
      .prepareAuthConfig()
      .pipe(
        mergeMap(
          (config: {}): Observable<DcpConsentTemplateData> =>
            this.http.post<DcpConsentTemplateData>(
              this.dcpKymaServiceConfigurator.getUrl('getConsentTemplate'),
              JSON.stringify(this.prepareGetConsentPayloadJson(user, productId)),
              config
            )
        )
      );
  }

  /**
   * Call Kyma service that communicates with the g2 api.
   * It is needed for authentication and product mapping.
   *
   * @param product Product the reviews should be retrieved for.
   * @param page page number of review results the user wants to view.
   * @param pageSize amount of results per page
   * @param exclusionList list of excluded reviews
   */
  public getReviewsForProduct(
    product: Product,
    page: number,
    pageSize: number,
    exclusionList?: Array<string>
  ): Observable<G2ReviewList> {
    let params: HttpParams = new HttpParams()
      .set('filter[product_id]', this.productUtilitiesService.getProductReviewsId(product))
      .set('page[size]', pageSize.toString())
      .set('page[number]', page.toString());

    if (exclusionList) {
      exclusionList.forEach((id) => {
        params = params.append('filter[excluding][]', id);
      });
    }

    const headers: HttpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/vnd.api+json')
      .set('dcp-product-id', product.code);
    return this.http
      .get<any>(this.dcpKymaServiceConfigurator.getUrl('getProductReviews') + '/reviews', {params, headers})
      .pipe(share());
  }

  /**
   * Get the reviews that was received from the call to the g2 api
   */
  public getReviewsData(): BehaviorSubject<G2ReviewList> {
    return this.reviewsData;
  }

  /**
   * Set the data that was received from the call to the g2 api
   *
   * @param reviews - List of the reviews that was received
   */
  public setReviewsData(reviews: G2ReviewList) {
    this.reviewsData.next(reviews);
  }

  /**
   * Call to Kyma lambda to save accepted by user consent to Hybris (backoffice) via occ API.
   *
   * @param user user to save consent for.
   * @param email user email address.
   * @param leadId id of the lead created.
   * @param consent consent accepted by user.
   */
  public saveCapturedConsent(user: LeadFormCreator, email: string, leadId: any, consent: DcpConsentTemplateData): void {
    this.http
      .post(
        this.dcpKymaServiceConfigurator.getUrl('saveCapturedConsent'),
        JSON.stringify(this.prepareSaveConsentPayloadJson(user, email, leadId, consent)),
        this.dcpKymaServiceConfigurator.prepareHeaderConfig()
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        error: (err) => {
          this.dcpGlobalMessageService.addMessage(err, this?.constructor?.name, 'errorLogging');
        },
      });
  }

  /**
   * Prepare authorization headers for Kyma
   */
  prepareAuthConfig(): Observable<HttpRequestOptions> {
    return this.dcpKymaServiceConfigurator.prepareAuthConfig();
  }

  /**
   * Prepare headers for kyma calls
   */
  prepareHeaderConfig(): HttpRequestOptions {
    return this.dcpKymaServiceConfigurator.prepareHeaderConfig();
  }

  /**
   * Prepare payload for Kyma get-consent call.
   *
   * @param user user to take user id from.
   * @param productId id of the product to get consent for.
   */
  private prepareGetConsentPayloadJson(user: LeadFormCreator, productId: string): {} {
    const bodyPayloadJson: any = {};
    bodyPayloadJson.consentTemplateId = this.partnerProductConsentTemplateId;
    bodyPayloadJson.userId = user?.uid || this.anonymousUserId;
    bodyPayloadJson.baseSiteId = this.configService.getStringArray('context.baseSite')[0];
    bodyPayloadJson.productId = productId;
    return bodyPayloadJson;
  }

  /**
   * Prepare payload for Kyma save-consent call.
   *
   * @param user user to take user id from.
   * @param email user email address.
   * @param leadId id of the lead created.
   * @param consent consent accepted by user on the lead form.
   */
  private prepareSaveConsentPayloadJson(
    user: LeadFormCreator,
    email: string,
    leadId: LeadFormModel,
    consent: DcpConsentTemplateData
  ): {} {
    const saveCapturedConsentPayloadJson: any = {};
    saveCapturedConsentPayloadJson.userId = (user?.uid || this.anonymousUserId).toString();
    saveCapturedConsentPayloadJson.orderId = leadId;
    saveCapturedConsentPayloadJson.email = (user?.email || email).toString();
    saveCapturedConsentPayloadJson.baseSiteId = this.configService.getStringArray('context.baseSite')[0];
    saveCapturedConsentPayloadJson.consentTemplateId = this.partnerProductConsentTemplateId;
    saveCapturedConsentPayloadJson.consentTemplateVersion = consent
      ? consent.version
        ? consent.version.toString()
        : this.defaultConsentTemplateVersion
      : this.defaultConsentTemplateVersion;
    return saveCapturedConsentPayloadJson;
  }

  /**
   * Returning headers and parameters for the call to upload Tax Exemption Certificate to backoffice Cart object.
   * Clearing Content-Type headers, so it substituted automatically to multipart/form together with
   * proper boundaries.
   *
   * @private
   */
  private getSaveFileToCartCallOptions(): Observable<HttpRequestOptions> {
    return this.getExemptionCertificateCallParams().pipe(
      switchMap((params) => {
        let headers = new HttpHeaders();
        return this.dcpKymaServiceConfigurator.prepareAuthConfig().pipe(
          map((authHeaders) => {
            headers = authHeaders.headers.delete('Content-Type');
            return {params, headers};
          })
        );
      })
    );
  }

  /**
   * Returning headers and parameters for the call to remove Tax Exemption Certificate from currently active cart.
   *
   * @private
   */
  private getRemoveFileFromCartCallOptions(): Observable<HttpRequestOptions> {
    return this.getExemptionCertificateCallParams().pipe(
      switchMap((params) =>
        this.dcpKymaServiceConfigurator.prepareAuthConfig().pipe(map((headers) => ({params, headers: headers.headers})))
      )
    );
  }

  /**
   * Parameters for Tax Exemption Certificate uploading/removal call (they're the same for both calls).
   *
   * @private
   */
  private getExemptionCertificateCallParams(): Observable<HttpParams> {
    return this.baseSiteService
      .getActive()
      .pipe(
        map((currentBaseSite) =>
          new HttpParams()
            .set(this.baseSiteIdHeaderName, currentBaseSite)
            .set(this.userIdHeaderName, this.currentUserId)
            .set(this.cartIdHeaderName, this.currentUserId)
        )
      );
  }

  /**
   * Returns the url of the kyma service as configured in the environment.
   *
   * @param namespace name of the url in the kyma namespace
   */
  getUrl(namespace: string): string {
    return this.dcpKymaServiceConfigurator.getUrl(namespace);
  }
}
